import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { AppContext } from '../App';
import { useFilters } from '../context/FilterContext';
import {
  Calculator, Users, DollarSign, TrendingDown, Clock, Plus, Minus,
  ArrowRight, CheckCircle2, AlertTriangle, Info, Sliders, UserPlus, Shuffle
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, LabelList
} from 'recharts';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const ALL_DEPTS_KEY = '__all__';

const fmtCurrency = (v) => `$${Math.round(v || 0).toLocaleString()}`;
const fmtNum = (v) => Math.round(v || 0).toLocaleString();

// Compute months between two YYYY-MM-DD strings (inclusive, fractional)
const monthsBetween = (start, end) => {
  if (!start || !end) return 12;
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s) || isNaN(e) || e < s) return 12;
  const days = (e - s) / (1000 * 60 * 60 * 24) + 1;
  return Math.max(days / 30.4375, 0.25);
};

const ScenarioModeling = () => {
  const { API } = useContext(AppContext);
  const { filters } = useFilters();

  const [currentData, setCurrentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scenario type: 'hire' (add staff) or 'redistribute' (cap OT per employee)
  const [mode, setMode] = useState('hire');

  // Scope
  const [selectedDept, setSelectedDept] = useState(ALL_DEPTS_KEY);

  // Hire inputs
  const [newHires, setNewHires] = useState(2);
  const [hourlyRate, setHourlyRate] = useState(18);
  const [otReductionPct, setOtReductionPct] = useState(40);
  const [benefitsCostPct, setBenefitsCostPct] = useState(30);

  // Redistribution inputs
  const [weeklyOtCap, setWeeklyOtCap] = useState(5); // hrs OT/week max per employee

  const periodMonths = useMemo(
    () => monthsBetween(filters?.start_date, filters?.end_date),
    [filters?.start_date, filters?.end_date]
  );
  const annualizationFactor = 12 / periodMonths;

  // Fetch overtime analysis grouped by department
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const body = {
          group_by: 'department',
          start_date: filters?.start_date || undefined,
          end_date: filters?.end_date || undefined,
          location: filters?.location || undefined,
          manager: filters?.manager || undefined,
          pay_types: filters?.pay_types?.length ? filters.pay_types : undefined,
        };
        const response = await axios.post(`${API}/overtime/analysis`, body);
        setCurrentData(response.data);
      } catch (err) {
        setError('Failed to load overtime data');
        toast.error('Failed to load overtime data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API, filters?.start_date, filters?.end_date, filters?.location, filters?.manager, filters?.pay_types]);

  const departments = useMemo(() => {
    const list = (currentData?.records || []).map(r => r.dimension).filter(Boolean);
    return [ALL_DEPTS_KEY, ...list];
  }, [currentData]);

  // Derive the scope stats (either one dept or sum of all)
  const scope = useMemo(() => {
    if (!currentData?.records?.length) return null;

    let otHours, cost, employees, totalHours, label;
    if (selectedDept === ALL_DEPTS_KEY) {
      const recs = currentData.records;
      otHours = recs.reduce((s, r) => s + (r.total_premium_hours || r.overtime_hours || 0), 0);
      cost = recs.reduce((s, r) => s + (r.cost || 0), 0);
      employees = recs.reduce((s, r) => s + (r.employee_count || 0), 0);
      totalHours = recs.reduce((s, r) => s + (r.total_hours || 0), 0);
      label = 'All Departments';
    } else {
      const r = currentData.records.find(x => x.dimension === selectedDept);
      if (!r) return null;
      otHours = r.total_premium_hours || r.overtime_hours || 0;
      cost = r.cost || 0;
      employees = r.employee_count || 0;
      totalHours = r.total_hours || 0;
      label = selectedDept;
    }
    return { label, otHours, cost, employees: Math.max(employees, 1), totalHours };
  }, [currentData, selectedDept]);

  // Scenario calculation
  const scenario = useMemo(() => {
    if (!scope) return null;

    const annualOTHours = scope.otHours * annualizationFactor;
    const annualOTCost = scope.cost * annualizationFactor;
    const avgOTRate = annualOTHours > 0 ? annualOTCost / annualOTHours : hourlyRate * 1.5;
    const otPerEmployee = scope.otHours / scope.employees; // in the selected period

    const annualSalaryPerHire = hourlyRate * 2080;
    const benefitsPerHire = annualSalaryPerHire * (benefitsCostPct / 100);
    const totalCostPerHire = annualSalaryPerHire + benefitsPerHire;

    let otHoursReduced = 0;
    let addedHeadcount = 0;
    let addedCost = 0;
    let assumption = '';

    if (mode === 'hire') {
      addedHeadcount = newHires;
      otHoursReduced = annualOTHours * (otReductionPct / 100);
      addedCost = totalCostPerHire * newHires;
      assumption = `${newHires} new hire(s) absorb ${otReductionPct}% of current OT`;
    } else {
      // Redistribute: cap weekly OT per employee
      const weeksInPeriod = periodMonths * 4.345;
      const currentAnnualOtPerEmp = otPerEmployee * annualizationFactor;
      const currentWeeklyOtPerEmp = otPerEmployee / Math.max(weeksInPeriod, 1);

      if (currentWeeklyOtPerEmp <= weeklyOtCap) {
        // No redistribution needed
        otHoursReduced = 0;
        addedHeadcount = 0;
        addedCost = 0;
        assumption = `Current OT (${currentWeeklyOtPerEmp.toFixed(1)} hrs/week/employee) is already below the cap of ${weeklyOtCap} hrs/week. No action needed.`;
      } else {
        const annualCapPerEmp = weeklyOtCap * 52;
        const excessAnnualHrsPerEmp = Math.max(currentAnnualOtPerEmp - annualCapPerEmp, 0);
        const totalExcessAnnualHrs = excessAnnualHrsPerEmp * scope.employees;

        // Convert excess OT hours into regular-time equivalent and compute headcount
        // Each new hire provides 2080 regular hrs/year
        addedHeadcount = Math.ceil(totalExcessAnnualHrs / 2080);
        otHoursReduced = totalExcessAnnualHrs;
        addedCost = totalCostPerHire * addedHeadcount;
        assumption = `Cap OT at ${weeklyOtCap} hrs/week/employee. Excess ${fmtNum(totalExcessAnnualHrs)} annual OT hrs require ${addedHeadcount} additional hire(s).`;
      }
    }

    const otCostSaved = otHoursReduced * avgOTRate;
    const netAnnualSavings = otCostSaved - addedCost;
    const paybackMonths = netAnnualSavings > 0
      ? Math.ceil(addedCost / (netAnnualSavings / 12))
      : null;
    const afterOTHours = Math.max(annualOTHours - otHoursReduced, 0);
    const afterOTCost = Math.max(annualOTCost - otCostSaved, 0);
    const afterEmployees = scope.employees + addedHeadcount;
    const afterOTPerEmployee = afterEmployees > 0 ? afterOTHours / afterEmployees : 0;
    const roi = addedCost > 0 ? ((netAnnualSavings) / addedCost) * 100 : 0;

    return {
      label: scope.label,
      currentEmployees: scope.employees,
      otPerEmployee: otPerEmployee * annualizationFactor, // annual OT/employee
      annualOTCost,
      annualOTHours,
      avgOTRate,
      addedHeadcount,
      addedCost,
      costPerHire: totalCostPerHire,
      otHoursReduced,
      otCostSaved,
      netAnnualSavings,
      paybackMonths,
      afterOTHours,
      afterOTCost,
      afterOTPerEmployee,
      roi,
      assumption,
    };
  }, [scope, mode, newHires, hourlyRate, otReductionPct, benefitsCostPct, weeklyOtCap, annualizationFactor, periodMonths]);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto animate-pulse space-y-6" data-testid="scenario-modeling-loading">
        <div className="h-8 w-64 bg-slate-200 rounded"></div>
        <div className="grid grid-cols-3 gap-4">
          {['a','b','c'].map(k => <div key={`skel-${k}`} className="h-48 bg-slate-100 rounded-xl"></div>)}
        </div>
      </div>
    );
  }

  if (error || !currentData?.records?.length) {
    return (
      <div className="p-6 max-w-7xl mx-auto" data-testid="scenario-modeling-empty">
        <h1 className="text-3xl font-bold text-[#1e3a5f] flex items-center gap-3 mb-2">
          <Calculator className="h-8 w-8" /> Scenario Planner
        </h1>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-amber-800 flex gap-3 items-start">
          <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">No overtime data available for the current filters.</p>
            <p className="text-sm mt-1">Adjust your date range or clear filters to model a scenario.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="scenario-modeling-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
          <Calculator className="h-8 w-8" />
          Scenario Planner
        </h1>
        <p className="text-muted-foreground mt-1">
          Model the impact of hiring or redistributing workloads on overtime costs.
          <span className="ml-2 text-xs text-slate-400">
            Period: {periodMonths.toFixed(1)} months (annualized × {annualizationFactor.toFixed(2)})
          </span>
        </p>
      </div>

      {/* Mode Toggle */}
      <div className="mb-6 inline-flex bg-slate-100 rounded-lg p-1" data-testid="scenario-mode-toggle">
        <button
          onClick={() => setMode('hire')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition ${
            mode === 'hire' ? 'bg-white shadow text-[#2E5C8A]' : 'text-slate-600 hover:text-slate-800'
          }`}
          data-testid="scenario-mode-hire"
        >
          <UserPlus className="h-4 w-4" /> Hire New Staff
        </button>
        <button
          onClick={() => setMode('redistribute')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition ${
            mode === 'redistribute' ? 'bg-white shadow text-[#2E5C8A]' : 'text-slate-600 hover:text-slate-800'
          }`}
          data-testid="scenario-mode-redistribute"
        >
          <Shuffle className="h-4 w-4" /> Redistribute Workload
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Controls */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-6"
        >
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Sliders className="h-5 w-5 text-[#2E5C8A]" /> Scenario Inputs
          </h3>

          {/* Scope */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Scope</label>
            <select
              value={selectedDept}
              onChange={e => setSelectedDept(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C8A]/30"
              data-testid="scenario-dept-select"
            >
              {departments.map(d => (
                <option key={d} value={d}>
                  {d === ALL_DEPTS_KEY ? 'All Departments' : d}
                </option>
              ))}
            </select>
          </div>

          {mode === 'hire' && (
            <>
              {/* New Hires */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-600 mb-1.5">New Hires</label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setNewHires(Math.max(1, newHires - 1))}
                    className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200"
                    data-testid="scenario-hires-minus"
                    aria-label="Decrease hires"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="text-2xl font-bold text-[#2E5C8A] w-12 text-center" data-testid="scenario-hires-count">
                    {newHires}
                  </span>
                  <button
                    onClick={() => setNewHires(newHires + 1)}
                    className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200"
                    data-testid="scenario-hires-plus"
                    aria-label="Increase hires"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* OT Reduction */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-600 mb-1.5">
                  Expected OT Reduction: <span className="text-[#FF8C42] font-bold">{otReductionPct}%</span>
                </label>
                <input
                  type="range" min={10} max={80} value={otReductionPct}
                  onChange={e => setOtReductionPct(Number(e.target.value))}
                  className="w-full accent-[#FF8C42]"
                  data-testid="scenario-reduction-slider"
                />
                <div className="flex justify-between text-xs text-slate-400"><span>10%</span><span>80%</span></div>
              </div>
            </>
          )}

          {mode === 'redistribute' && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-600 mb-1.5">
                Weekly OT Cap: <span className="text-[#FF8C42] font-bold">{weeklyOtCap} hrs/week</span> per employee
              </label>
              <input
                type="range" min={0} max={20} value={weeklyOtCap}
                onChange={e => setWeeklyOtCap(Number(e.target.value))}
                className="w-full accent-[#FF8C42]"
                data-testid="scenario-cap-slider"
              />
              <div className="flex justify-between text-xs text-slate-400"><span>0</span><span>20</span></div>
              <p className="text-xs text-slate-500 mt-2">
                Excess OT above the cap is absorbed by new hires at a regular rate.
              </p>
            </div>
          )}

          {/* Hourly Rate */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-slate-600 mb-1.5">
              Hourly Rate (new hires): <span className="text-[#2E5C8A] font-bold">${hourlyRate}</span>
            </label>
            <input
              type="range" min={12} max={50} value={hourlyRate}
              onChange={e => setHourlyRate(Number(e.target.value))}
              className="w-full accent-[#2E5C8A]"
              data-testid="scenario-rate-slider"
            />
            <div className="flex justify-between text-xs text-slate-400"><span>$12</span><span>$50</span></div>
          </div>

          {/* Benefits */}
          <div className="mb-2">
            <label className="block text-sm font-medium text-slate-600 mb-1.5">
              Benefits Cost: <span className="text-[#4CAF93] font-bold">{benefitsCostPct}%</span> of salary
            </label>
            <input
              type="range" min={15} max={50} value={benefitsCostPct}
              onChange={e => setBenefitsCostPct(Number(e.target.value))}
              className="w-full accent-[#4CAF93]"
              data-testid="scenario-benefits-slider"
            />
            <div className="flex justify-between text-xs text-slate-400"><span>15%</span><span>50%</span></div>
          </div>
        </motion.div>

        {/* Right: Results */}
        <div className="lg:col-span-2 space-y-6">
          {scenario && (
            <>
              {/* Net Savings Banner */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl p-5 border ${
                  scenario.netAnnualSavings > 0
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
                data-testid="scenario-savings-banner"
              >
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      {scenario.netAnnualSavings > 0 ? (
                        <CheckCircle2 className="h-6 w-6 text-green-600" />
                      ) : (
                        <AlertTriangle className="h-6 w-6 text-red-600" />
                      )}
                      <h3 className={`text-lg font-bold ${scenario.netAnnualSavings > 0 ? 'text-green-800' : 'text-red-800'}`}>
                        {scenario.netAnnualSavings > 0 ? 'Net Annual Savings' : 'Net Annual Cost Increase'}
                      </h3>
                    </div>
                    <p
                      className={`text-3xl font-bold mt-1 ${scenario.netAnnualSavings > 0 ? 'text-green-700' : 'text-red-700'}`}
                      data-testid="scenario-net-savings"
                    >
                      {fmtCurrency(Math.abs(scenario.netAnnualSavings))}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">for {scenario.label}</p>
                  </div>
                  <div className="text-right space-y-1">
                    <div className="text-sm">
                      <span className="text-slate-500">ROI:</span>
                      <span
                        className={`ml-2 font-bold ${scenario.roi > 0 ? 'text-green-700' : 'text-red-700'}`}
                        data-testid="scenario-roi"
                      >
                        {Math.round(scenario.roi)}%
                      </span>
                    </div>
                    {scenario.paybackMonths != null && (
                      <div className="text-sm">
                        <span className="text-slate-500">Payback:</span>
                        <span className="ml-2 font-bold text-[#2E5C8A]" data-testid="scenario-payback">
                          {scenario.paybackMonths} months
                        </span>
                      </div>
                    )}
                    <div className="text-sm">
                      <span className="text-slate-500">Headcount:</span>
                      <span className="ml-2 font-bold text-[#2E5C8A]" data-testid="scenario-headcount">
                        +{scenario.addedHeadcount}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                    <DollarSign className="h-3.5 w-3.5" /> Current Annual OT
                  </div>
                  <p className="text-xl font-bold text-red-500" data-testid="scenario-current-ot-cost">{fmtCurrency(scenario.annualOTCost)}</p>
                  <p className="text-xs text-slate-400">{fmtNum(scenario.annualOTHours)} hours</p>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                    <TrendingDown className="h-3.5 w-3.5" /> Projected Annual OT
                  </div>
                  <p className="text-xl font-bold text-[#4CAF93]" data-testid="scenario-projected-ot-cost">{fmtCurrency(scenario.afterOTCost)}</p>
                  <p className="text-xs text-slate-400">{fmtNum(scenario.afterOTHours)} hours</p>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                    <Users className="h-3.5 w-3.5" /> Hiring Cost
                  </div>
                  <p className="text-xl font-bold text-[#2E5C8A]" data-testid="scenario-hiring-cost">{fmtCurrency(scenario.addedCost)}</p>
                  <p className="text-xs text-slate-400">
                    {scenario.addedHeadcount} hire(s) @ {fmtCurrency(scenario.costPerHire)}/yr
                  </p>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                    <Clock className="h-3.5 w-3.5" /> OT per Employee
                  </div>
                  <p className="text-xl font-bold text-slate-700">
                    {fmtNum(scenario.otPerEmployee)}
                    <ArrowRight className="inline h-4 w-4 text-slate-400 mx-1" />
                    {fmtNum(scenario.afterOTPerEmployee)}
                  </p>
                  <p className="text-xs text-slate-400">hours/employee (annual)</p>
                </motion.div>
              </div>

              {/* Cost Comparison Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl border border-slate-200 p-5"
                data-testid="scenario-chart"
              >
                <h3 className="font-semibold text-slate-800 mb-4">Annual Cost Comparison</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={[
                      {
                        label: 'Current',
                        overtime: Math.round(scenario.annualOTCost),
                        hiring: 0,
                        total: Math.round(scenario.annualOTCost),
                      },
                      {
                        label: 'After Scenario',
                        overtime: Math.round(scenario.afterOTCost),
                        hiring: Math.round(scenario.addedCost),
                        total: Math.round(scenario.afterOTCost + scenario.addedCost),
                      },
                    ]}
                    barSize={80}
                    margin={{ top: 20, right: 20, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 13 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={v => fmtCurrency(v)} />
                    <Legend />
                    <Bar dataKey="overtime" name="Overtime Cost" stackId="a" fill="#FF8C42">
                      <LabelList dataKey="overtime" position="insideTop" fill="#fff" fontSize={11} formatter={v => v > 0 ? `$${(v/1000).toFixed(0)}k` : ''} />
                    </Bar>
                    <Bar dataKey="hiring" name="New Hire Cost" stackId="a" fill="#2E5C8A" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="total" position="top" fill="#334155" fontSize={12} formatter={v => `$${(v/1000).toFixed(0)}k`} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </motion.div>

              {/* Assumptions */}
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-sm text-slate-600" data-testid="scenario-assumptions">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="h-4 w-4" />
                  <span className="font-medium">Assumptions</span>
                </div>
                <ul className="space-y-1 ml-6 list-disc text-slate-500">
                  <li>{scenario.assumption}</li>
                  <li>Annual salary = hourly rate × 2,080 hours (52 weeks × 40 hrs)</li>
                  <li>Benefits estimated at {benefitsCostPct}% of base salary</li>
                  <li>
                    Data annualized from {periodMonths.toFixed(1)} months of timesheet records
                    (factor × {annualizationFactor.toFixed(2)})
                  </li>
                  <li>
                    Avg OT rate derived from actual cost ÷ hours = {fmtCurrency(scenario.avgOTRate)}/hr
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScenarioModeling;
